import {
  Box,
  Flex,
  Input,
  Spacer,
  Text,
  VStack,
  HStack,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Textarea,
  Circle,
  Icon,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  Card,
  Space,
  Spin,
  Table,
  Button,
  message,
  Select,
  Tag,
  Row,
  Col, // Add Select from antd
} from "antd";
import { useMemo, useState, useCallback, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { debounce } from "lodash";
import {
  CalendarOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { useUsersMaster } from "../../store/users/reducer";
import { useDispatch } from "react-redux";
import { REQUEST_MASTER_USERS } from "../../store/users/userstActionTypes";
import { render } from "react-dom";

const { Option } = Select; // Destructure Option from Select

const CreditMasterTable: React.FC = () => {
  const [creditData, setCreditData] = useState<any | null>(null);
  const [searchText, setSearchText] = useState("");
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedRewardType, setSelectedRewardType] = useState("all");
  // Add filter state
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const { masterUsers, busy } = useUsersMaster();

  const dispatch = useDispatch();

  // console.log("masterUsers:", masterUsers);
  useEffect(() => {
    // if (!busy && !(masterCategory || []).length) {
    dispatch({ type: REQUEST_MASTER_USERS });
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Issue Credit Modal States
  const [isIssueCreditModalOpen, setIsIssueCreditModalOpen] = useState(false);
  const [issuingCredit, setIssuingCredit] = useState(false);
  const [creditForm, setCreditForm] = useState({
    credit: 1,
    description: "",
    expiry: new Date(),
    creditType: "REWARD",
  });
  const [formErrors, setFormErrors] = useState({
    credit: "",
    description: "",
    expiry: "",
    creditType: "",
  });

  // Filter options
  // const filterOptions = [
  //   { value: "all", label: "All" },
  //   { value: "active", label: "Active" },
  //   { value: "redeemed", label: "Redeemed" },
  //   { value: "expired", label: "Expired" },
  // ];

  // Function to determine if a credit entry is expired
  const isExpired = (expiryDate: string) => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    return expiry < today;
  };

  // Function to filter credit data based on selected filter
  const getFilteredCreditData = useMemo(() => {
    if (!creditData || creditData?.length === 0) return [];

    if (filterStatus === "all") {
      return creditData;
    }

    return creditData?.filter((entry: any) => {
      switch (filterStatus) {
        case "active":
          return entry.status !== "redeemed" && !isExpired(entry.expiry);
        case "redeemed":
          return entry.status === "redeemed";
        case "expired":
          return isExpired(entry.expiry) && entry.status !== "redeemed";
        default:
          return true;
      }
    });
  }, [creditData, filterStatus]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setUsers([]);
        setShowDropdown(false);
        return;
      }

      setSearchLoading(true);
      let tkn = localStorage.getItem("token");
      if (!tkn) {
        return;
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/admin/user/list?q=${searchQuery}`
          // {
          //   headers: {
          //     Authorization: `${tkn}`,
          //   },
          // }
        );
        // console.log("Search results:", response.data);
        setSelectedStatus("all");
        setSelectedRewardType("all");
        setUsers(response.data.data || []);
        setShowDropdown(true);
      } catch (err) {
        console.log("Search error:", err);
        setUsers([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    []
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  // Handle user selection
  const handleUserSelect = async (user: any) => {
    setSelectedUser(user);
    setSearchText(user.name);
    setShowDropdown(false);
    setLoading(true);
    // Reset filter to "all" when selecting new user
    setFilterStatus("all");

    let tkn = localStorage.getItem("token");
    if (!tkn) {
      return;
    }
    // console.log("User token:", user);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin/credit/user/${user._id}`
        // {
        //   headers: {
        //     Authorization: `${tkn}`,
        //   },
        // }
      );
      // console.log("User credit data:", response.data);

      setCreditData(response.data.data || []);
    } catch (err) {
      console.log("Error fetching user credit data:", err);
      setCreditData(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter change
  // const handleFilterChange = (value: string) => {
  //   setFilterStatus(value);
  // };

  // Handle Issue Credit button click
  const handleIssueCredit = () => {
    if (!selectedUser) {
      message.warning("Please select a user first");
      return;
    }
    setIsIssueCreditModalOpen(true);
  };

  // Handle credit form changes
  const handleCreditFormChange = (field: string, value: any) => {
    setCreditForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing/changing
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {
      credit: "",
      description: "",
      expiry: "",
      creditType: "",
    };

    // Validate credit
    if (!creditForm.credit || creditForm.credit <= 0) {
      errors.credit = "Please enter a valid credit amount greater than 0";
    }

    // Validate description
    if (!creditForm.description.trim()) {
      errors.description = "Please add description";
    }

    // Validate expiry date
    if (!creditForm.expiry) {
      errors.expiry = "Please select expiry date";
    } else if (creditForm.expiry <= new Date()) {
      errors.expiry = "Expiry date must be in the future";
    }

    if (!creditForm.creditType) {
      errors.creditType = "Please select credit type";
    }

    setFormErrors(errors);

    // Return true if no errors
    return !errors.credit && !errors.description && !errors.expiry;
  };

  // Handle credit form submission
  const handleSubmitCredit = async () => {
    if (!selectedUser) {
      message.error("No user selected");
      return;
    }

    // Validate form first
    if (!validateForm()) {
      return;
    }

    setIssuingCredit(true);
    // let tkn = localStorage.getItem("token");

    try {
      const payload = {
        user: selectedUser._id,
        credit: creditForm.credit,
        description: creditForm.description,
        creditType: creditForm.creditType,
        expiry: creditForm.expiry.toISOString(),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/credit/add`, // Adjust endpoint as needed
        payload
        // {
        //   headers: {
        //     Authorization: `${tkn}`,
        //     'Content-Type': 'application/json'
        //   },
        // }
      );

      message.success("Credit issued successfully!");
      setIsIssueCreditModalOpen(false);

      // Reset form
      setCreditForm({
        credit: 1,
        description: "",
        expiry: new Date(),
        creditType: "REWARD",
      });

      // Clear errors
      setFormErrors({
        credit: "",
        description: "",
        expiry: "",
        creditType: "",
      });

      // Refresh credit data for the selected user
      handleUserSelect(selectedUser);
    } catch (err: any) {
      console.log("Error issuing credit:", err);
      message.error(err.response?.data?.message || "Failed to issue credit");
    } finally {
      setIssuingCredit(false);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsIssueCreditModalOpen(false);
    // Reset form when closing
    setCreditForm({
      credit: 1,
      description: "",
      expiry: new Date(),
      creditType: "REWARD",
    });
    // Clear errors
    setFormErrors({
      credit: "",
      description: "",
      expiry: "",
      creditType: "",
    });
  };

  // Process credit data for display - Updated to use filtered data

  const processedData = useMemo(() => {
    if (!creditData || creditData?.length === 0) return null;

    // Use filtered data for calculations
    const filteredData = getFilteredCreditData;

    const totalCredits = filteredData?.reduce((sum: number, entry: any) => {
      return sum + (entry.credit || 0);
    }, 0);

    const totalActualCredits = filteredData?.reduce(
      (sum: number, entry: any) => {
        return sum + (entry.actualCredit || 0);
      },
      0
    );

    const creditsUsed = filteredData?.reduce((sum: number, entry: any) => {
      return sum + (entry.status === "redeemed" ? entry.credit || 0 : 0);
    }, 0);

    return {
      totalCredits,
      totalActualCredits,
      creditsUsed,
      userName: creditData[0]?.user
        ? `${creditData[0].user.firstName} ${creditData[0].user.lastName}`
        : "Unknown User",
      filteredCount: filteredData.length,
      totalCount: creditData.length,
    };
  }, [creditData, getFilteredCreditData]);

  // Table columns for credit data
  // const creditColumns = [
  //   {
  //     title: "S.No.",
  //     dataIndex: "index",
  //     width: 80,
  //     render: (_: any, __: any, index: number) => index + 1,
  //   },
  //   {
  //     title: "Credit",
  //     dataIndex: "credit",
  //     width: 100,
  //     render: (credit: number) => `${credit} Credits`,
  //   },
  //   {
  //     title: "Actual Credit",
  //     dataIndex: "actualCredit",
  //     width: 120,
  //     render: (actualCredit: number) => `${actualCredit} Credits`,
  //   },
  //   {
  //     title: "Reward Type",
  //     dataIndex: "rewardtype",
  //     width: 120,
  //   },
  //   {
  //     title: "Status",
  //     dataIndex: "status",
  //     width: 100,
  //     render: (status: string, record: any) => {
  //       // Check if expired but not redeemed
  //       const expired = isExpired(record.expiry);
  //       if (expired && status !== "redeemed") {
  //         return <Badge colorScheme="gray">Expired</Badge>;
  //       }
  //       return (
  //         <Badge colorScheme={status === "redeemed" ? "red" : "green"}>
  //           {status.charAt(0).toUpperCase() + status.slice(1)}
  //         </Badge>
  //       );
  //     },
  //   },
  //   {
  //     title: "Expiry Date",
  //     dataIndex: "expiry",
  //     width: 120,
  //     render: (expiry: string) => {
  //       const expiryDate = new Date(expiry);
  //       const today = new Date();
  //       const differenceInTime = expiryDate.getTime() - today.getTime();
  //       const differenceInDays = Math.ceil(
  //         differenceInTime / (1000 * 3600 * 24)
  //       );

  //       let expiryColor;
  //       if (differenceInDays <= 0) {
  //         expiryColor = "red";
  //       } else if (differenceInDays <= 7) {
  //         expiryColor = "orange";
  //       } else {
  //         expiryColor = "green";
  //       }

  //       return (
  //         <Text fontSize="sm" marginBottom={0}>
  //           {expiryDate.toLocaleDateString()}
  //         </Text>
  //         // <VStack spacing={1}>
  //         // {/* <Badge colorScheme={expiryColor} fontSize="xs">
  //         //   {differenceInDays <= 0
  //         //     ? "Expired"
  //         //     : `${differenceInDays} days left`}
  //         // </Badge> */}
  //         // </VStack>
  //       );
  //     },
  //   },
  //   {
  //     title: "Description",
  //     dataIndex: "description",
  //     width: 300,
  //     render: (description: string) => (
  //       <Text fontSize="sm" noOfLines={2} title={description}>
  //         {description}
  //       </Text>
  //     ),
  //   },
  // ];
  // console.log("Processed Data:", processedData);
  const totalCreditsEarned = creditData?.reduce(
    (sum: any, credit: any) => sum + credit.totalCredit,
    0
  );
  const totalBalance = creditData?.reduce(
    (sum: any, credit: any) => sum + credit.balance,
    0
  );
  const totalRedeemed = totalCreditsEarned - totalBalance;

  const allTransactions = useMemo(() => {
    const transactions: any[] = [];

    creditData?.forEach((credit: any) => {
      credit.history?.forEach((transaction: any) => {
        transactions.push({
          ...transaction,
          creditId: credit._id,
          rewardType: credit.rewardType,
          totalCredit: credit.totalCredit,
          currentBalance: credit.balance,
          expiry: credit.expiry,
          creditDescription: credit.description,
        });
      });
    });

    return transactions?.sort(
      (a, b) => new Date(b?.date).getTime() - new Date(a?.date).getTime()
    );
  }, [creditData]);

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    return allTransactions?.filter((transaction) => {
      const matchesStatus =
        selectedStatus === "all" ||
        (selectedStatus === "credit" && transaction.type === "CREATED") ||
        (selectedStatus === "debit" &&
          (transaction.type === "REDEEMED" ||
            transaction.type === "PARTIAL_REDEEMED" ||
            transaction.type === "EXPIRED"));

      const matchesRewardType =
        selectedRewardType === "all" ||
        transaction.rewardType === selectedRewardType;
      return matchesStatus && matchesRewardType;
    });
  }, [allTransactions, selectedStatus, selectedRewardType]);

  const formatDate = (dateString: any) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // const getTransactionIcon = (type: any) => {
  //   switch (type) {
  //     case "CREATED":
  //       return <TrendingUp />;
  //     case "REDEEMED":
  //     case "PARTIAL_REDEEMED":
  //       return <TrendingDown />;
  //     default:
  //       return <CreditCard />;
  //   }
  // };

  // const getRewardTypeColor = (type: any) => {
  //   switch (type) {
  //     case "BONUS":
  //       return "bg-blue-100 text-blue-800";
  //     case "REFERRAL":
  //       return "bg-green-100 text-green-800";
  //     case "CANCELLED_BOOKING":
  //       return "bg-orange-100 text-orange-800";
  //     default:
  //       return "bg-gray-100 text-gray-800";
  //   }
  // };

  const getTransactionIconAntd = (type: any) => {
    switch (type) {
      case "CREATED":
        return <PlusCircleOutlined style={{ color: "#16a34a" }} />;
      case "REDEEMED":
      case "PARTIAL_REDEEMED":
        return <MinusCircleOutlined style={{ color: "#dc2626" }} />;
      default:
        return <PlusCircleOutlined style={{ color: "#6b7280" }} />;
    }
  };

  // Helper function to get reward type color for Ant Design Tags
  const getRewardTypeTagColor = (rewardType: any) => {
    switch (rewardType) {
      case "BONUS":
        return "blue";
      case "REFERRAL":
        return "green";
      case "CANCELLED_BOOKING":
        return "orange";
      default:
        return "default";
    }
  };
  const columns = [
    {
      title: "Date & Time",
      dataIndex: "date",
      key: "date",
      width: 160,
      render: (date: any) => (
        <Space>
          <CalendarOutlined style={{ color: "#9ca3af", fontSize: "14px" }} />
          <span style={{ fontSize: "14px", color: "#111827" }}>
            {formatDate(date)}
          </span>
        </Space>
      ),
    },
    {
      title: "Transaction",
      dataIndex: "description",
      key: "transaction",
      width: 300,
      render: (description: string, record: any) => (
        <Space align="start">
          <div style={{ marginTop: "2px" }}>
            {getTransactionIconAntd(record.type)}
          </div>
          <div>
            <div
              style={{ fontWeight: 500, fontSize: "14px", color: "#111827" }}
            >
              {description}
            </div>
            {record.creditDescription && (
              <div
                style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}
              >
                {record.creditDescription}
              </div>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type: any) => {
        const isCredit = type === "CREATED";
        const isPartial = type === "PARTIAL_REDEEMED";

        return (
          <Tag
            color={isCredit ? "success" : "error"}
            style={{
              fontSize: "12px",
              fontWeight: 600,
              borderRadius: "9999px",
              padding: "4px 16px",
              textAlign: "center",
            }}
          >
            {isCredit ? "Credit" : isPartial ? "Partial Debit" : "Debit"}
          </Tag>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (amount: any, record: any) => (
        <span
          style={{
            fontFamily: "monospace",
            fontSize: "14px",
            fontWeight: 600,
            color: record.type === "CREATED" ? "#16a34a" : "#dc2626",
          }}
        >
          {record.type === "CREATED" ? "+" : "-"}
          {amount}
        </span>
      ),
    },
    {
      title: "Reward Type",
      dataIndex: "rewardType",
      key: "rewardType",
      width: 150,
      render: (rewardType: any) => (
        <Tag
          color={getRewardTypeTagColor(rewardType)}
          style={{
            fontSize: "12px",
            fontWeight: 600,
            borderRadius: "9999px",
            padding: "4px 16px",
            textAlign: "center",
          }}
        >
          {rewardType.replace("_", " ")}
        </Tag>
      ),
    },
    {
      title: "Credit ID",
      dataIndex: "creditId",
      key: "creditId",
      width: 100,
      render: (creditId: any) => (
        <span
          style={{
            fontSize: "12px",
            color: "#6b7280",
            fontFamily: "monospace",
          }}
        >
          {creditId.slice(-8)}
        </span>
      ),
    },
  ];

  const userColumns = [
    {
      title: "Nos.",
      dataIndex: "index",
      width: 60,
      render: (_: any, __: any, index: number) => `#${index + 1}`,
    },
    {
      title: "Login Icon",
      dataIndex: "Image",
      width: 100,
      render: (_: any, record: any) => {
        const imageSrc = (() => {
          if (record.originalData.isEmailLogin) return "/icons/email.svg";
          if (record.originalData.isPhoneLogin) return "/icons/phone.svg";
          if (record.originalData.isGoogleLogin) return "/icons/google.svg";
          if (record.originalData.isAppleLogin) return "/icons/apple.svg";
          if (record.originalData.isGuestLogin) return "/icons/guest.svg";
          // return "/icons/default.svg";
          return "/icons/usermanagement.svg";
        })();

        return (
          <img
            src={imageSrc}
            alt="Login Type"
            style={{
              width: "20px",
              height: "20px",
              // objectFit: "contain",
              // borderRadius: "50%",
            }}
          />
        );
      },
    },
    {
      title: "First Name",
      dataIndex: "firstName",
      width: 120,
    },
    {
      title: "Last name",
      dataIndex: "lastName",
      width: 120,
    },
    {
      title: "Email",
      dataIndex: "email",
      width: 200,
    },
  ];

  const formatRelativeData = (relatives: any[] | null | undefined) => {
    if (!Array.isArray(relatives) || relatives.length === 0) return "-";

    return relatives
      .filter((rel) => rel && rel.name && rel.birthday)
      .map(
        (rel) => `${rel.name} & ${new Date(rel.birthday).toLocaleDateString()}`
      )
      .join(", ");
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(masterUsers)) {
      console.log("masterUsers is not an array:", masterUsers);
      return [];
    }

    const transformed = masterUsers
      .filter(
        (u) => !u.isGuestLogin && !/^delete_[a-z]+@ht\.com$/.test(u.email)
      )
      .map((value: any) => {
        return {
          key: value._id,
          name: `${value.firstName || ""} ${value.middleName || ""} ${
            value.lastName || ""
          }`.trim(),
          firstName: value.firstName || "-",
          lastName: value.lastName || "-",
          email: value.email || "-",
          phoneNumber: value.phoneNumber || "-",
          birthday: value.birthday
            ? new Date(value.birthday).toLocaleDateString()
            : "-",
          preferedDays: Array.isArray(value.preferedDays)
            ? value.preferedDays.join(", ")
            : "-",
          relativeBirthdays: formatRelativeData(value.relativeBirthdays),
          relativeAnniversary: formatRelativeData(value.relativeAnniversary),
          originalData: value,
        };
      });

    return transformed;
  }, [masterUsers]);
  // console.log("filteredTransactions User:", filteredTransactions);
  // console.log("selectedStatus", selectedStatus);

  return (
    <div
      style={{
        width: "96vw",
        maxWidth: "96vw",
        maxHeight: "86vh",
        height: "86vh",
        top: "8vh",
        left: "4vw",
        overflowY: "auto",
        position: "relative",
        backgroundColor: "#EEEEEE",
        padding: "30px",
      }}
    >
      <VStack spacing={6} align="stretch">
        {/* Search Section */}
        <Box position="relative">
          <Flex w="full" alignItems="center" justifyContent="space-between">
            <Box
              display="flex"
              alignItems="center"
              bg="#FFF"
              border="1px solid #b2b2b233"
              borderRadius="12px"
              w="498px"
              padding="8px"
              position="relative"
            >
              <Box
                pl={2}
                display="flex"
                alignItems="center"
                color="#9E9E9E"
                fontSize="2xl"
              >
                {searchLoading ? (
                  <Spin size="small" />
                ) : (
                  <img
                    src="/icons/searchlogo.svg"
                    alt="searchlogo"
                    style={{ height: "24px", width: "24px" }}
                  />
                )}
              </Box>

              <Input
                id="search"
                type="text"
                placeholder="Search users by name..."
                value={searchText}
                onChange={handleSearchChange}
                autoComplete="off"
                fontSize="sm"
                border="none"
                outline="none"
                color="#757575"
                _focus={{
                  border: "none",
                  outline: "none",
                  boxShadow: "none",
                }}
                bg={"transparent"}
                style={{
                  outline: "none",
                  boxShadow: "none",
                }}
              />
            </Box>
            <Spacer />

            {/* Add Filter Dropdown */}
            {/* {selectedUser && (
              <Select
                value={filterStatus}
                onChange={handleFilterChange}
                style={{ width: 120, marginRight: 16 }}
                placeholder="Filter"
              >
                {filterOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            )} */}

            <Button
              type="primary"
              onClick={handleIssueCredit}
              disabled={!selectedUser}
            >
              Issue Credit
            </Button>
          </Flex>

          {/* User Dropdown */}
          {showDropdown && users.length > 0 && (
            <Box
              position="absolute"
              top="60px"
              left="0"
              zIndex={1000}
              bg="white"
              border="1px solid #e2e8f0"
              borderRadius="12px"
              boxShadow="lg"
              w="498px"
              maxH="300px"
              overflowY="auto"
            >
              {users.map((user: any) => (
                <Box
                  key={user.id}
                  p={3}
                  cursor="pointer"
                  _hover={{ bg: "#f7fafc" }}
                  borderBottom="1px solid #e2e8f0"
                  onClick={() => handleUserSelect(user)}
                >
                  <Text fontWeight="500" marginBottom={0}>
                    {user.firstName} {user.lastName}
                  </Text>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <Table
          rowKey="key"
          columns={userColumns}
          dataSource={filteredData}
          scroll={{ x: "max-content" }}
          size="middle"
          pagination={{
            position: ["bottomRight"],
            pageSize: 8, // ✅ fixed at 8 rows per page
            showSizeChanger: false,
          }}
          style={{
            backgroundColor: "white",
            borderRadius: 6,
            padding: 10,
          }}
          rowClassName={(record: any) => record.color}
          loading={busy && filteredData.length === 0}
          onRow={(record: any) => {
            return {
              onClick: () =>
                handleUserSelect({
                  _id: record.key,
                  name: `${record.firstName} ${record.lastName}`,
                }),
              style: { cursor: "pointer" }, // ✅ makes whole row look clickable
            };
          }}
        />

        {/* User Credit Data Display */}
        {loading ? (
          <Box textAlign="center" py={10}>
            <Spin size="large" />
            <Text mt={4}>Loading user credit data...</Text>
          </Box>
        ) : selectedUser && processedData ? (
          <>
            <VStack spacing={6} align="stretch">
              {/* Credit Details Table */}
              <Card>
                <VStack spacing={4} align="stretch">
                  <HStack justifyContent="space-between" m={0}>
                    <Text fontSize="xl" fontWeight="semibold">
                      {processedData.userName}&apos;s Credit Details
                    </Text>
                    {filterStatus !== "all" && (
                      <Badge colorScheme="blue">
                        Showing {processedData.filteredCount} of{" "}
                        {processedData.totalCount} records
                      </Badge>
                    )}
                  </HStack>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} m={0}>
                    {/* Total Credits Earned */}
                    <Box
                      bg="white"
                      borderRadius="lg"
                      shadow="sm"
                      p={2}
                      pl={4}
                      border="1px"
                      borderColor="gray.200"
                      height={100}
                    >
                      <Flex align="center">
                        <Circle p={3} bg="blue.100">
                          <Icon w={6} h={6} color="blue.600" />
                        </Circle>
                        <Box ml={4}>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.600"
                          >
                            Total Credits Earned
                          </Text>
                          <Text
                            fontSize="2xl"
                            fontWeight="bold"
                            color="gray.900"
                          >
                            {totalCreditsEarned}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>

                    {/* Available Balance */}
                    <Box
                      bg="white"
                      borderRadius="lg"
                      shadow="sm"
                      p={2}
                      pl={4}
                      border="1px"
                      borderColor="gray.200"
                      height={100}
                    >
                      <Flex align="center">
                        <Circle p={3} bg="green.100">
                          <Icon w={6} h={6} color="green.600" />
                        </Circle>
                        <Box ml={4}>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.600"
                          >
                            Available Balance
                          </Text>
                          <Text
                            fontSize="2xl"
                            fontWeight="bold"
                            color="green.600"
                          >
                            {totalBalance}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>

                    {/* Credits Redeemed */}
                    <Box
                      bg="white"
                      borderRadius="lg"
                      shadow="sm"
                      p={2}
                      pl={4}
                      border="1px"
                      borderColor="gray.200"
                      height={100}
                    >
                      <Flex align="center">
                        <Circle p={3} bg="red.100">
                          <Icon w={6} h={6} color="red.600" />
                        </Circle>
                        <Box ml={4}>
                          <Text
                            fontSize="sm"
                            fontWeight="medium"
                            color="gray.600"
                          >
                            Credits Redeemed
                          </Text>
                          <Text
                            fontSize="2xl"
                            fontWeight="bold"
                            color="red.600"
                          >
                            {totalRedeemed}
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  </SimpleGrid>

                  <Box
                    bg="white"
                    borderRadius="lg"
                    shadow="sm"
                    py={6}
                    mb={1}
                    // border="1px"
                    // borderColor="gray.200"
                  >
                    <Row gutter={[16, 16]}>
                      {/* Status Filter */}
                      <Col xs={24} sm={12}>
                        <Select
                          value={selectedStatus}
                          onChange={(value) => setSelectedStatus(value)}
                          size="middle"
                          style={{ width: "100%" }}
                          bordered={true}
                          placeholder="Select status"
                          className="custom-select-padded"
                          dropdownStyle={{
                            borderRadius: "8px",
                            padding: "8px",
                          }}
                        >
                          <Option value="all">All Transactions</Option>
                          <Option value="credit">Credits Only</Option>
                          <Option value="debit">Debits Only</Option>
                        </Select>
                      </Col>

                      {/* Reward Type Filter */}
                      <Col xs={24} sm={12}>
                        <Select
                          value={selectedRewardType}
                          onChange={(value) => setSelectedRewardType(value)}
                          size="middle"
                          style={{ width: "100%" }}
                          bordered={true}
                          placeholder="Select reward type"
                          className="custom-select-padded"
                          dropdownStyle={{
                            borderRadius: "8px",
                            padding: "8px",
                          }}
                        >
                          <Option value="all">All Reward Types</Option>
                          <Option value="BONUS">Bonus</Option>
                          <Option value="REFERRAL">Referral</Option>
                          <Option value="CANCELLED_BOOKING">
                            Cancelled Booking
                          </Option>
                        </Select>
                      </Col>
                    </Row>
                  </Box>
                  <Table
                    dataSource={filteredTransactions}
                    columns={columns}
                    rowKey={(record, index) => `${record.creditId}-${index}`}
                    pagination={false}
                    scroll={{ x: 800 }}
                    rowClassName="hover-row"
                    style={{
                      backgroundColor: "white",
                    }}
                    size="middle"
                  />
                  {/* <Table
                  columns={creditColumns}
                  dataSource={getFilteredCreditData}
                  rowKey="_id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                  }}
                  scroll={{ x: 1000 }}
                /> */}
                </VStack>
              </Card>
            </VStack>
          </>
        ) : selectedUser && !processedData ? (
          <Box textAlign="center" py={10}>
            <Text color="gray.500">No credit data found for this user</Text>
          </Box>
        ) : (
          <Box textAlign="center" py={10}>
            <Text color="gray.500">
              Search and select a user to view their credit details
            </Text>
          </Box>
        )}
      </VStack>

      {/* Issue Credit Modal */}
      <Modal
        isOpen={isIssueCreditModalOpen}
        onClose={handleCloseModal}
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Issue Credit</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {selectedUser && (
                <Box w="full">
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    Issuing credit to:{" "}
                    <strong>
                      {selectedUser.firstName} {selectedUser.lastName}
                    </strong>
                  </Text>
                </Box>
              )}

              <FormControl isInvalid={!!formErrors.credit}>
                <FormLabel>Credit Amount</FormLabel>
                <NumberInput
                  value={creditForm.credit}
                  onChange={(valueString, valueNumber) =>
                    handleCreditFormChange("credit", valueNumber || 0)
                  }
                  min={1}
                >
                  <NumberInputField placeholder="Enter credit amount" />
                </NumberInput>
                {formErrors.credit && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    {formErrors.credit}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={!!formErrors.description}>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={creditForm.description}
                  onChange={(e) =>
                    handleCreditFormChange("description", e.target.value)
                  }
                  placeholder="Enter description for this credit"
                  resize="none"
                  rows={3}
                />
                {formErrors.description && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    {formErrors.description}
                  </Text>
                )}
              </FormControl>

              <FormControl isInvalid={!!formErrors.creditType}>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "500",
                      fontSize: "14px",
                    }}
                  >
                    Credit Type
                  </label>
                  <Select
                    value={creditForm.creditType}
                    onChange={(value) =>
                      handleCreditFormChange("creditType", value)
                    }
                    placeholder="Select credit type"
                    style={{ width: "100%" }}
                    size="middle"
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  >
                    <Option value="REWARD">Reward</Option>
                    <Option value="BONUS">Bonus</Option>
                  </Select>
                  {formErrors.creditType && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#ff4d4f",
                        marginTop: "4px",
                      }}
                    >
                      {formErrors.creditType}
                    </div>
                  )}
                </div>
              </FormControl>

              <FormControl isInvalid={!!formErrors.expiry}>
                <FormLabel>Expiry Date</FormLabel>
                <Box w="full">
                  <DatePicker
                    selected={creditForm.expiry}
                    onChange={(date) =>
                      handleCreditFormChange("expiry", date || new Date())
                    }
                    dateFormat="dd/MM/yyyy"
                    minDate={new Date()}
                    placeholderText="Select expiry date"
                    customInput={
                      <Input
                        bg="white"
                        border="1px solid #e2e8f0"
                        _hover={{ borderColor: "#cbd5e0" }}
                        _focus={{
                          borderColor: "#3182ce",
                          boxShadow: "0 0 0 1px #3182ce",
                        }}
                      />
                    }
                  />
                </Box>
                {formErrors.expiry && (
                  <Text fontSize="sm" color="red.500" mt={1}>
                    {formErrors.expiry}
                  </Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button onClick={handleCloseModal} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSubmitCredit}
              loading={issuingCredit}
            >
              Issue Credit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default CreditMasterTable;
